import { I18n } from "@assets/strings";
import React from "react";
import styled, { css } from "styled-components";
import { ExerciseType } from "@domain/lms/course/exercise/exercise";
import { ExerciseDraft } from "@domain/lms/course/exercise/exerciseDraft";
import { BigPlusButton } from "@ui/components/buttons/bigPlusButton";
import { ButtonsSelector } from "@ui/components/buttonsSelector";
import { DeleteIconBig } from "@ui/components/icons";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { MaterialTextInput } from "@ui/components/inputs/materialTextInput";
import { Row } from "@ui/components/layout";
import { gap } from "@ui/styles/utils";
import { useAutoArrayFocus } from "@ui/pages/lms/courseEditorPage/hooks";
import { ExerciseController } from "./exerciseController";

interface ExerciseEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	exercise: ExerciseDraft;
	controller: ExerciseController;
	index?: number;
}
export const ExerciseEditor: React.FC<ExerciseEditorProps> = ({ exercise, controller, index, ...htmlProps }) => {
	const { title, choices, comment } = exercise;
	useAutoArrayFocus(choices.toArray());

	return (
		<Container {...htmlProps}>
			<div>
				<Title>
					<Row gap={20} align="center">
						<div>{index !== undefined ? I18n.t("course.section.qcm.number", { index: index + 1 }) : null}</div>
						<ButtonsSelector
							options={[
								{ id: "UNIQUE" as ExerciseType, label: "Choix unique" },
								{ id: "MULTIPLE" as ExerciseType, label: "Choix multiples" },
							]}
							value={exercise.type}
							onChange={(v) => controller.setType(v)}
						/>
					</Row>
				</Title>
				<MaterialTextInput
					value={title}
					placeholder={I18n.t("course.editor.qcm.title.placeholder")}
					onChange={(v) => controller.setTitle(v)}
				/>
			</div>
			<Answers>
				{choices.map((choice, i) => (
					<ChoiceWrapper key={choice.id}>
						<Choice>
							<ChoiceIndex isRight={choice.valid} isMultiple={exercise.type === "MULTIPLE"}>
								{i + 1}
							</ChoiceIndex>
							<StyledInput
								id={choice.id}
								value={choice.text}
								placeholder={I18n.t("course.editor.qcm.answer.placeholder", { index: i + 1 })}
								onChange={(v) => controller.setChoiceText(i, v)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										controller.addChoice();
									}
									if (e.key === "Backspace" && !choice.text && choices.size > 1) {
										controller.removeChoice(i);
									}
								}}
							/>
						</Choice>
						{choices.size > 1 && <DeleteIconBig onClick={() => controller.removeChoice(i)} />}
					</ChoiceWrapper>
				))}
			</Answers>
			<NewAnswerButton label={I18n.t("course.editor.qcm.answer.add")} onClick={() => controller.addChoice()} />
			<div>
				<Title>{I18n.t("course.editor.qcm.choose_answer")}</Title>
				<ChoiceSelector>
					{choices.map(({ valid, id }, i) => (
						<ChoiceIndex
							isRight={valid}
							key={id}
							onClick={() => controller.setChoiceValid(i)}
							isMultiple={exercise.type === "MULTIPLE"}
						>
							{i + 1}
						</ChoiceIndex>
					))}
				</ChoiceSelector>
			</div>
			<div>
				<Title>{I18n.t("course.editor.qcm.comment.title")}</Title>
				<CommentInput
					key={index}
					placeholder={I18n.t("course.editor.qcm.comment.placeholder")}
					value={comment}
					onChange={(value) => controller.setComment(value)}
				/>
			</div>
		</Container>
	);
};

const Container = styled.div`
	${gap(40)}
	display: flex;
	flex-direction: column;
	align-items: stretch;

	> button {
		align-self: flex-start;
	}
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.primary};
	font-weight: bold;
	margin-bottom: 20px;
`;

const Answers = styled.div`
	${gap(15)}
`;

const ChoiceWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;

	> svg {
		cursor: pointer;
	}
`;

const StyledInput = styled(MaterialTextInput)`
	flex: 1;
`;

const ChoiceIndex = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isRight" && prop !== "isMultiple",
})<{ isRight?: boolean; isMultiple?: boolean }>`
	${({ theme }) => theme.textStyles.tertiary};
	line-height: 0.8125rem;
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${({ isMultiple }) => (isMultiple ? "4px" : "10px")};
	transition: border-radius 0.1s linear;
	border: solid 1px ${({ theme, isRight }) => (isRight ? theme.colors.primary : theme.colors.gray)};
	${({ theme, isRight }) =>
		isRight &&
		css`
			background-color: ${theme.colors.primary};
			color: ${theme.colors.white};
		`}
`;

const Choice = styled.div`
	display: flex;
	align-items: center;
	flex: 1;

	${ChoiceIndex} {
		margin-right: 10px;
	}
`;

const ChoiceSelector = styled.div`
	display: flex;
	${gap(14, "row")};
	${ChoiceIndex} {
		cursor: pointer;
	}
`;

const CommentInput = styled(RichTextInput)`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	padding: 10px 12px 9px;
	border-radius: 16px;
	font-size: 0.8125rem;
	min-height: 200px;
	width: 100%;
`;

const NewAnswerButton = styled(BigPlusButton)`
	padding: 18px;
`;
